import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { useApp } from "@/context/AppContext";
import API from "@/lib/api";
import { geocode } from "@/lib/geo";
import { ArrowLeft, Search, Info, FileText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function PublishRequest() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const targetBusiness = params.get("business");
  const targetBusinessName = params.get("businessName");
  const { getCategory, catName } = useApp();
  const cat = getCategory(slug);

  const [subcategory, setSubcategory] = useState(params.get("sub"));
  const [other, setOther] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [when, setWhen] = useState("this_week");
  const [askedAs, setAskedAs] = useState("private");
  const [budget, setBudget] = useState("");
  const [isOnline, setIsOnline] = useState(false);
  const [maxAvail, setMaxAvail] = useState(5);
  const [cityQuery, setCityQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loc, setLoc] = useState(null);
  const [suggested, setSuggested] = useState(null);

  useEffect(() => {
    API.get("/me/full").then((r) => {
      const z = r.data.zone;
      if (z?.start_point?.city) { setLoc(z.start_point); setCityQuery(z.start_point.city); }
    }).catch(() => {});
  }, []);

  if (!cat) return <Layout><p className="text-[#4A4E68]">Categoria non trovata.</p></Layout>;
  const isOther = subcategory === "altro";
  const isVarie = cat.slug === "varie";
  const noAttachments = cat.no_attachments;

  const searchCity = async () => {
    const res = await geocode(cityQuery);
    setResults(res);
  };

  const submit = async () => {
    if (!title.trim()) return toast.error("Aggiungi un titolo");
    if (isOther && !other.trim()) return toast.error("Descrivi il tipo di servizio");
    let location = loc || {};
    if (!isOnline && !location.lat && cityQuery) {
      const res = await geocode(cityQuery);
      if (res[0]) location = { city: res[0].city, lat: res[0].lat, lng: res[0].lng, address: res[0].city };
    }
    location = { ...location, is_online: isOnline };
    try {
      const res = await API.post("/requests", {
        category: slug, subcategory: isVarie ? null : subcategory, other_description: isOther ? other : null,
        title, description, location, when, asked_as: askedAs,
        budget: budget ? parseFloat(budget) : null, max_availabilities: maxAvail, language: "it",
        target_business_id: targetBusiness || null,
      });
      if (res.data.suggested_category) { setSuggested(res.data.suggested_category); }
      toast.success("Richiesta pubblicata!");
      navigate(`/request/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  return (
    <Layout>
      <button onClick={() => navigate("/")} className="inline-flex items-center gap-1 text-[#4A4E68] mb-3 text-sm"><ArrowLeft size={16} /> Home</button>
      <h1 className="font-serif text-2xl font-bold text-[#0F1230] mb-1">{catName(cat)}</h1>
      <p className="text-sm text-[#4A4E68] mb-4">Racconta di cosa hai bisogno. Bastano pochi tocchi.</p>

      {targetBusiness && (
        <div className="flex gap-2 bg-[#FFF6DA] border border-[#F6DC7A] rounded-xl p-3 mb-4 text-sm text-[#141A3A]" data-testid="quote-target-banner">
          <FileText size={16} className="shrink-0 mt-0.5" /> Preventivo indirizzato solo a <b className="font-semibold">{targetBusinessName}</b>
        </div>
      )}

      <div className="bg-white rounded-[18px] border border-[#DADFEA] p-5 space-y-4" data-testid="publish-request-form">
        {!isVarie && cat.subcategories?.length > 0 && (
          <div>
            <p className="text-sm font-medium text-[#0F1230] mb-2">Cosa ti serve</p>
            <div className="flex flex-wrap gap-2">
              {cat.subcategories.map((s) => (
                <button key={s.slug} data-testid={`sub-${s.slug}`} onClick={() => setSubcategory(s.slug)}
                  className={`px-3 h-10 rounded-full text-sm border ${subcategory === s.slug ? "bg-[#1F4FE0] text-white border-[#1F4FE0]" : "border-[#DADFEA] text-[#2A2E48]"}`}>
                  {catName(s)}
                </button>
              ))}
            </div>
          </div>
        )}

        {isOther && (
          <div>
            <p className="text-sm font-medium text-[#0F1230] mb-1">Descrivi il tipo di servizio</p>
            <Input data-testid="other-description" maxLength={60} placeholder={cat.placeholder_other} value={other} onChange={(e) => setOther(e.target.value)} />
          </div>
        )}

        <div>
          <p className="text-sm font-medium text-[#0F1230] mb-1">In due parole</p>
          <Input data-testid="publish-title" placeholder={cat.placeholder_other || "es. serve una mano..."} value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>

        <div>
          <p className="text-sm font-medium text-[#0F1230] mb-1">Descrizione (facoltativa)</p>
          <Textarea data-testid="publish-description" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>

        <div>
          <p className="text-sm font-medium text-[#0F1230] mb-1">Luogo</p>
          {cat.can_be_online && (
            <label className="flex items-center gap-2 mb-2 text-sm text-[#2A2E48]">
              <Switch data-testid="online-switch" checked={isOnline} onCheckedChange={setIsOnline} /> Si può fare online
            </label>
          )}
          {!isOnline && (
            <>
              <div className="flex gap-2">
                <Input data-testid="city-input" placeholder="Città o indirizzo" value={cityQuery} onChange={(e) => setCityQuery(e.target.value)} />
                <Button type="button" onClick={searchCity} variant="outline" className="rounded-full shrink-0"><Search size={16} /></Button>
              </div>
              {results.length > 0 && (
                <div className="mt-2 border border-[#DADFEA] rounded-xl overflow-hidden">
                  {results.map((r, i) => (
                    <button key={i} onClick={() => { setLoc({ city: r.city, lat: r.lat, lng: r.lng, address: r.label }); setCityQuery(r.city); setResults([]); }}
                      className="block w-full text-left px-3 py-2 text-sm hover:bg-[#EEF1F7] border-b border-[#DADFEA] last:border-0">{r.label}</button>
                  ))}
                </div>
              )}
              {loc?.city && <p className="text-xs text-[#2E7D32] mt-1">Posizione: {loc.city}</p>}
            </>
          )}
        </div>

        <div>
          <p className="text-sm font-medium text-[#0F1230] mb-1">Quando</p>
          <div className="flex gap-2">
            {[["today", "Oggi"], ["this_week", "In settimana"], ["whenever", "Quando capita"]].map(([v, l]) => (
              <button key={v} data-testid={`when-${v}`} onClick={() => setWhen(v)} className={`px-3 h-10 rounded-full text-sm border ${when === v ? "bg-[#1F4FE0] text-white border-[#1F4FE0]" : "border-[#DADFEA]"}`}>{l}</button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-sm font-medium text-[#0F1230] mb-1">Chiedo come</p>
          <div className="flex gap-2">
            {[["private", "Privato"], ["company", "Azienda"], ["entity", "Ente"]].map(([v, l]) => (
              <button key={v} data-testid={`asked-${v}`} onClick={() => setAskedAs(v)} className={`px-3 h-10 rounded-full text-sm border ${askedAs === v ? "bg-[#1F4FE0] text-white border-[#1F4FE0]" : "border-[#DADFEA]"}`}>{l}</button>
            ))}
          </div>
        </div>

        {!cat.no_price && (
          <div>
            <p className="text-sm font-medium text-[#0F1230] mb-1">Budget indicativo (facoltativo)</p>
            <Input data-testid="publish-budget" type="number" placeholder="€" value={budget} onChange={(e) => setBudget(e.target.value)} />
          </div>
        )}

        {noAttachments && (
          <div className="flex gap-2 bg-[#FFF1E6] border border-[#FFD8BF] rounded-xl p-3 text-xs text-[#B8420A]">
            <Info size={14} className="shrink-0 mt-0.5" /> Per Baby sitter e Ripetizioni non si possono inserire foto o nomi di bambini e ragazzi.
          </div>
        )}

        <div>
          <p className="text-sm font-medium text-[#0F1230] mb-1">Quante disponibilità accettare: {maxAvail}</p>
          <input data-testid="max-avail" type="range" min="1" max="10" value={maxAvail} onChange={(e) => setMaxAvail(parseInt(e.target.value))} className="w-full accent-[#141A3A]" />
        </div>

        <Button data-testid="publish-submit-button" onClick={submit} className="w-full h-12 bg-[#FFD23F] hover:bg-[#F2C200] text-[#0F1230] text-base rounded-full">Pubblica richiesta</Button>
        <p className="text-xs text-[#4A4E68] text-center">La richiesta resta attiva 15 giorni, rinnovabile.</p>
      </div>
    </Layout>
  );
}
